/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractFormComponent, BuilderUserData, CommonActions, FormActionPipelines, FormFlowState } from '@gh/builder-shared';
import { ApplicationView, HelpRequestPriority, HelpRequestType } from '@gh/builder-shared-data';
import { ImageViewerDialogComponent } from '@gh/common-internal-shared';
import { ActionFlow, EnumDictionary, getDialogInitData } from '@gh/common-shared';
import {
  DialogInput,
  DialogService,
  TinymceSettings,
  tmSettingsBuilder,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';
import { assign } from 'lodash';
import {
  getHelpRequestScreenshot,
  HelpBugRequestBusinessService,
  HelpBugRequestDialogConfig,
  HelpRequest,
} from '../../resources';
import { BugRequestFormState } from './bug-request-form.service';

@Component({
  selector: 'gh-bug-request-dialog',
  template: require('./bug-request-dialog.component.html'),
  styles: [require('./bug-request-dialog.component.scss')],
  viewProviders: [BugRequestFormState],
})
export class HelpBugRequestDialogComponent extends AbstractFormComponent<HelpRequest>
  implements OnInit, DialogInput<HelpBugRequestDialogConfig> {

  tmSettings: TinymceSettings;

  input: HelpBugRequestDialogConfig;

  priorityDict: EnumDictionary<HelpRequestPriority>;

  office: string;
  masterZone?: string ;

  constructor(public businessService: HelpBugRequestBusinessService,
              public dialogRef: MatDialogRef<HelpBugRequestDialogComponent>,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              public form: BugRequestFormState,
              private dialogService: DialogService) {
    super();
  }

  get requestMode(): boolean {
    return this.input.mode === HelpRequestType.Request;
  }

  get title(): string {
    return this.requestMode ? 'lbl_send_request' : 'lbl_send_bug';
  }

  get completeFlow(): ActionFlow<FormFlowState<HelpRequest>> {
    return this.pipelines.complete({
      execute: this.commonActions.execute((state) => this.businessService.create(<any>state.value)),
      finish: (state: FormFlowState<HelpRequest>) => this.dialogRef.close(state.form),
    });
  }

  ngOnInit(): void {
    const { request, ...init } = getDialogInitData(this.dialogData);

    assign(this, init);
    this.form.build(request);
    this.initApplicationView();
    this.tmSettings = tmSettingsBuilder()
      .scayt()
      .resizable(200, 300) // tslint:disable-line:no-magic-numbers
      .build();
  }

  onCancel(): void {
    if (this.form.dirty || this.form.value.mode === HelpRequestType.SystemBug) {
      this.dialogService.confirmation(yesNoConfirmation('msg_help_request_cancel')).pipe(
        filter$(yesAnswer))
        .subscribe(() => this.dialogRef.close());
    } else {
      this.dialogRef.close();
    }
  }

  openScreenshot(): void {
    const screenshot = getHelpRequestScreenshot(this.form.value)!; // tslint:disable-line:no-non-null-assertion
    this.dialogService.openWithInput(
      ImageViewerDialogComponent,
      { title: screenshot.name, url: screenshot.body },
      { panelClass: 'image-viewer-overlay' });
  }

  private initApplicationView(): void {
    const userData = this.businessService.userContext.data as BuilderUserData;
    switch (userData.appView) {
      case ApplicationView.Corporate:
        this.masterZone = '';
        this.office = '';
        break;
      case ApplicationView.Master:
        this.office = '';
        if (userData.masterZoneName) {
          this.masterZone = userData.masterZoneName;
        }
        break;
      case ApplicationView.Office:
        if (userData.masterZoneName) {
          this.masterZone = userData.masterZoneName;
          this.form.getControl('masterZoneName').setValue(this.masterZone);
        }
        if (userData.office) {
          this.office = userData.office.name;
        }
        break;
      default:
        break;
    }
  }
}
